import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  ColumnChooser,
  FilterPanel,
  FilterRow,
  GroupPanel,
  Grouping,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  StateStoring,
} from 'devextreme-react/data-grid';
import LoadPanel from 'devextreme-react/load-panel';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import notify from 'devextreme/ui/notify';
import {
  ProviderAccount,
  createProviderAccount,
  getCompanies,
  getProviderAccounts,
  getStores,
  updateProviderAccount,
} from '../../api/hub.service';
import './provider-accounts.scss';
import { useAuth } from '../../contexts/auth';

type AccountForm = {
  accountReference: string;
  apiBaseUrl: string;
  apiKey: string;
  storeCodes: string[];
  status: 'enabled' | 'disabled';
  expectedVersion?: number;
};

const emptyAccount = (): AccountForm => ({
  accountReference: '',
  apiBaseUrl: '',
  apiKey: '',
  storeCodes: [],
  status: 'enabled',
});

export default function ProviderAccountsPage() {
  const { user } = useAuth();
  const canWriteAccounts = user?.scopes.includes('provider_accounts:write') === true;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyId, setCompanyId] = useState(searchParams.get('companyId') ?? '');
  const [popup, setPopup] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<AccountForm>(emptyAccount);

  const companies = useQuery({ queryKey: ['companies'], queryFn: getCompanies });
  const stores = useQuery({
    queryKey: ['stores', companyId],
    queryFn: () => getStores(companyId),
    enabled: Boolean(companyId),
  });
  const accounts = useQuery({
    queryKey: ['provider-accounts', companyId],
    queryFn: () => getProviderAccounts(companyId),
    enabled: Boolean(companyId),
    retry: false,
  });

  useEffect(() => {
    if (!companyId && companies.data?.length) setCompanyId(companies.data[0].id);
  }, [companies.data, companyId]);
  useEffect(() => {
    if (companyId) setSearchParams({ companyId }, { replace: true });
  }, [companyId, setSearchParams]);

  const selectedCompany = companies.data?.find((company) => company.id === companyId);
  const activeStores = useMemo(() => (stores.data ?? []).filter((store) => store.active), [stores.data]);

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        return updateProviderAccount(companyId, form.accountReference, {
          apiBaseUrl: form.apiBaseUrl,
          ...(form.apiKey ? { apiKey: form.apiKey } : {}),
          storeCodes: form.storeCodes,
          status: form.status,
          expectedVersion: form.expectedVersion!,
        });
      }
      return createProviderAccount(companyId, {
        accountReference: form.accountReference,
        apiBaseUrl: form.apiBaseUrl,
        apiKey: form.apiKey,
        storeCodes: form.storeCodes,
        status: form.status,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['provider-accounts', companyId] });
      setPopup(false);
      setForm(emptyAccount());
      notify(editing ? 'Conta atualizada.' : 'Conta criada.', 'success', 1800);
    },
    onError: (error: any) => notify(error?.response?.data?.message ?? 'Não foi possível salvar a conta.', 'error', 3500),
  });

  const openNew = () => {
    setEditing(false);
    setForm(emptyAccount());
    setPopup(true);
  };
  const openEdit = (account: ProviderAccount) => {
    setEditing(true);
    setForm({
      accountReference: account.accountReference,
      apiBaseUrl: account.apiBaseUrl,
      apiKey: '',
      storeCodes: account.storeCodes,
      status: account.status,
      expectedVersion: account.version,
    });
    setPopup(true);
  };

  const toggleStore = (code: string, checked: boolean) => {
    setForm((current) => ({
      ...current,
      storeCodes: checked
        ? Array.from(new Set([...current.storeCodes, code]))
        : current.storeCodes.filter((storeCode) => storeCode !== code),
    }));
  };

  return (
    <div className="page-body hub-page provider-page">
      <LoadPanel visible={companies.isLoading || stores.isLoading || accounts.isLoading || save.isPending} />
      <div className="hub-page-header">
        <div>
          <span className="hub-eyebrow">Integrações</span>
          <h2>Contas ElosGate</h2>
          <p>Credenciais por empresa e loja. A chave é enviada uma única vez e não pode ser consultada.</p>
        </div>
        {canWriteAccounts && <Button
          text="Nova conta"
          icon="plus"
          type="default"
          disabled={!companyId || stores.isError || activeStores.length === 0}
          onClick={openNew}
        />}
      </div>

      <div className="provider-company-card">
        <div className="provider-company-filter">
          <label htmlFor="company-selector">Empresa</label>
          <SelectBox
            id="company-selector"
            dataSource={companies.data ?? []}
            valueExpr="id"
            displayExpr={(company) => company ? `${company.name} · Série ${company.serialNumber}` : ''}
            value={companyId}
            placeholder="Selecione a empresa"
            searchEnabled
            showClearButton={false}
            stylingMode="outlined"
            onValueChanged={(event) => setCompanyId(event.value ?? '')}
          />
          {selectedCompany && <span className="provider-locator"><strong>Identificador Tenant</strong>{selectedCompany.tenantLocatorType} / {selectedCompany.tenantLocatorValue}</span>}
        </div>
      </div>

      {companyId && stores.isSuccess && activeStores.length === 0 && (
        <div className="hub-warning">Nenhuma loja controlada foi encontrada na base operacional desta empresa.</div>
      )}
      {companyId && stores.isError && (
        <div className="hub-error">Não foi possível consultar as lojas na base operacional. Verifique o roteamento da empresa no PresenceTenant.</div>
      )}
      {accounts.isError && (
        <div className="hub-error">Não foi possível consultar o PresencePay. Verifique o backend e a configuração administrativa.</div>
      )}

      <div className="hub-grid-shell content-block">
        <DataGrid
          className="hub-grid"
          dataSource={accounts.data ?? []}
          keyExpr="accountReference"
          showBorders={false}
          showColumnLines={false}
          showRowLines
          rowAlternationEnabled
          hoverStateEnabled
          allowColumnReordering
          allowColumnResizing
          columnAutoWidth
          columnHidingEnabled
          noDataText={companyId ? 'Nenhuma conta configurada' : 'Selecione uma empresa'}
          onRowDblClick={canWriteAccounts ? (event) => openEdit(event.data) : undefined}
        >
          <StateStoring enabled type="localStorage" storageKey="presence-hub-provider-accounts-grid" />
          <SearchPanel visible width={310} placeholder="Pesquisar contas..." />
          <GroupPanel visible emptyPanelText="Arraste uma coluna aqui para agrupar" />
          <Grouping contextMenuEnabled autoExpandAll={false} />
          <ColumnChooser enabled mode="select" title="Colunas da grade" />
          <HeaderFilter visible />
          <FilterRow visible applyFilter="auto" />
          <FilterPanel visible />
          <Paging defaultPageSize={20} />
          <Pager visible allowedPageSizes={[10, 20, 50]} showPageSizeSelector showInfo showNavigationButtons />
          <Column dataField="accountReference" caption="Referência" minWidth={190} />
          <Column dataField="apiBaseUrl" caption="Endpoint" minWidth={280} />
          <Column dataField="storeCodes" caption="Lojas" calculateDisplayValue={(row) => row.storeCodes.join(', ')} minWidth={150} />
          <Column
            dataField="status"
            caption="Status"
            width={125}
            calculateDisplayValue={(row) => row.status === 'enabled' ? 'Habilitada' : 'Desabilitada'}
            cellRender={({ data }) => <span className={`hub-status ${data.status === 'enabled' ? 'hub-status--positive' : 'hub-status--neutral'}`}>{data.status === 'enabled' ? 'Habilitada' : 'Desabilitada'}</span>}
          />
          <Column dataField="version" caption="Versão" width={90} alignment="center" />
          <Column
            dataField="credentialConfigured"
            caption="Credencial"
            dataType="boolean"
            width={135}
            cellRender={({ value }) => <span className={`hub-status ${value ? 'hub-status--positive' : 'hub-status--warning'}`}>{value ? 'Configurada' : 'Pendente'}</span>}
          />
          {canWriteAccounts && <Column type="buttons" caption="Ação" width={72} buttons={[{ icon: 'edit', hint: 'Editar ou rotacionar', onClick: (event) => openEdit(event.row?.data) }]} />}
        </DataGrid>
      </div>
      <div className="hub-grid-meta">
        <span>{accounts.data?.length ?? 0} contas nesta empresa</span>
        {canWriteAccounts && <span>Duplo clique para editar ou rotacionar a credencial</span>}
      </div>

      <Popup
        visible={popup}
        title={editing ? `Editar ${form.accountReference}` : 'Nova conta ElosGate'}
        width={() => Math.min(640, window.innerWidth - 24)}
        height="auto"
        showCloseButton
        dragEnabled={false}
        onHiding={() => setPopup(false)}
      >
        <div className="hub-popup-content">
          <div className="write-only-notice">
            <strong>Chave protegida</strong>
            <span>{editing ? 'Deixe a chave vazia para manter a atual. Informe uma nova somente para rotacionar.' : 'A chave será enviada ao cofre do PresencePay e não ficará disponível para leitura.'}</span>
          </div>
          <div className="hub-form-fields">
            <label>Referência estável *<input disabled={editing} value={form.accountReference} onChange={(event) => setForm((current) => ({ ...current, accountReference: event.target.value }))} /></label>
            <label>Endpoint da API *<input type="url" value={form.apiBaseUrl} onChange={(event) => setForm((current) => ({ ...current, apiBaseUrl: event.target.value }))} /></label>
            <label>{editing ? 'Nova API Key (opcional)' : 'API Key *'}<input type="password" autoComplete="new-password" value={form.apiKey} onChange={(event) => setForm((current) => ({ ...current, apiKey: event.target.value }))} /></label>
            <fieldset className="provider-store-options">
              <legend>Lojas atendidas *</legend>
              {activeStores.map((store) => (
                <label key={store.id}>
                  <input type="checkbox" checked={form.storeCodes.includes(store.code)} onChange={(event) => toggleStore(store.code, event.target.checked)} />
                  <span>{store.code} · {store.name}</span>
                </label>
              ))}
            </fieldset>
            <label>Status
              <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as AccountForm['status'] }))}>
                <option value="enabled">Habilitada</option>
                <option value="disabled">Desabilitada</option>
              </select>
            </label>
          </div>
          <div className="hub-popup-actions">
            <Button text="Cancelar" onClick={() => setPopup(false)} />
            <Button text={editing ? 'Salvar alterações' : 'Criar conta'} type="default" onClick={() => save.mutate()} />
          </div>
        </div>
      </Popup>
    </div>
  );
}
